import React, { useState, useEffect } from 'react';
import {
  Search,
  CheckCircle2,
  XCircle,
  AlertCircle,
  User,
  MapPin,
  Calendar,
  MoreVertical,
  SlidersHorizontal as Filters,
  X,
  Users,
  Clock9,
  Clock,
  Loader2,
  Plus
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import SummaryCard from '../../../components/Dashboard/SummaryCard/SummaryCard';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import styles from './Overtime.module.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import OTRequestDrawer from './OTRequestDrawer';
import Pagination from '../../../components/Common/Pagination/Pagination';

const MySwal = withReactContent(Swal);

const OTRequests = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1
  });
  const [currentPage, setCurrentPage] = useState(1);


  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/overtime/requests');
      if (response.data && response.data.success) {
        // Handle paginated or plain arrays
        const rawList = response.data.data?.data || response.data.data || [];
        const mapped = rawList.map(req => ({
          id: req.id,
          employee: req.employee ? req.employee.employee_name : `Employee ID: ${req.employee_id}`,
          employee_id: req.employee_id,
          project_site: req.project ? req.project.project_name : 'N/A',
          date: req.request_date || (req.ot_date ? new Date(req.ot_date).toISOString().split('T')[0] : '') || '-',
          reason: req.reason || req.remarks || req.assignment_reason || '-',
          status: req.status || 'pending'
        }));
        setRequests(mapped);
      }
    } catch (error) {
      console.error('Failed to fetch OT requests:', error);
      toast.error('Failed to load overtime requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const pendingCount = requests.filter(r => r.status === 'pending' || r.status === 'assigned' || r.status === 'in_progress').length;
  const approvedCount = requests.filter(r => r.status === 'approved' || r.status === 'completed').length;
  const rejectedCount = requests.filter(r => r.status === 'rejected' || r.status === 'cancelled').length;
  const uniqueRequestors = new Set(requests.map(r => r.employee_id)).size;

  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved' || s === 'completed') return styles['status-approved'];
    if (s === 'rejected' || s === 'cancelled') return styles['status-rejected'];
    return styles['status-pending'];
  };

  const getStatusLabel = (status) => {
    const s = status?.toLowerCase();
    if (s === 'assigned') return 'Assigned';
    if (s === 'in_progress') return 'In Progress';
    if (s === 'completed') return 'Completed';
    if (s === 'cancelled') return 'Cancelled';
    if (s === 'approved') return 'Approved';
    if (s === 'rejected') return 'Rejected';
    return 'Pending';
  };

  const getStatusIcon = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved' || s === 'completed') return <CheckCircle2 size={12} />;
    if (s === 'rejected' || s === 'cancelled') return <XCircle size={12} />;
    return <AlertCircle size={12} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const summaryData = [
    {
      icon: <AlertCircle size={20} />,
      label: 'Pending Requests',
      value: String(pendingCount).padStart(2, '0'),
      trend: 'Total',
      trendType: 'neutral',
      iconBg: '#f59e0b'
    },
    {
      icon: <CheckCircle2 size={20} />,
      label: 'Approved Today',
      value: String(approvedCount).padStart(2, '0'),
      trend: 'Total',
      trendType: 'neutral',
      iconBg: '#10b981'
    },
    {
      icon: <Users size={20} />,
      label: 'Requesting Staff',
      value: String(uniqueRequestors).padStart(2, '0'),
      trend: 'Unique',
      trendType: 'neutral',
      iconBg: '#3b82f6'
    }
  ];

  const handleAction = (record, action) => {
    const isApprove = action === 'approve';

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: isApprove
        ? '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> Yes, Approve</div>'
        : '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg> Yes, Reject</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      input: !isApprove ? 'text' : undefined,
      inputPlaceholder: !isApprove ? 'Reason for rejection...' : undefined,
      inputValidator: !isApprove ? (value) => {
        if (!value) {
          return 'You must provide a reason for rejection!';
        }
      } : undefined,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: isApprove ? 'custom-swal-confirm-approve' : 'custom-swal-confirm-reject',
        cancelButton: 'custom-swal-cancel',
        input: 'custom-swal-input',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm-approve,
            .custom-swal-confirm-reject,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm-approve {
            background-color: #10b981 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
          }
          .custom-swal-confirm-approve:hover {
            background-color: #059669 !important;
          }
          .custom-swal-confirm-reject {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
          }
          .custom-swal-confirm-reject:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
          .custom-swal-input {
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 14px !important;
            font-size: 14px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 100% !important;
            box-sizing: border-box !important;
            margin: 16px 0 0 0 !important;
            outline: none !important;
            box-shadow: none !important;
          }
          .custom-swal-input:focus {
            border-color: #3b82f6 !important;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
          }
          .swal2-validation-message {
            background: transparent !important;
            color: #dc2626 !important;
            font-size: 13px !important;
            margin: 8px 0 0 0 !important;
            padding: 0 !important;
            font-family: 'Outfit', sans-serif !important;
            justify-content: center !important;
          }
        </style>
        <div class="custom-icon-wrapper" style="background-color: ${isApprove ? '#dcfce7' : '#fee2e2'};">
          ${isApprove
          ? `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`
          : `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>`
        }
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">
          ${isApprove ? 'Approve OT Request?' : 'Reject OT Request?'}
        </h2>
        <p style="margin: 0 0 0px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          ${isApprove
          ? `Are you sure you want to approve the OT request for <strong>${record.employee}</strong>?<br/>This will allow the employee to punch in for OT.`
          : `Are you sure you want to reject the OT request for <strong>${record.employee}</strong>?`
        }
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (isApprove) {
            await api.post(`/overtime/requests/${record.id}/approve`, {
              approval_remarks: 'Approved by Admin'
            });
          } else {
            await api.post(`/overtime/requests/${record.id}/reject`, {
              approval_remarks: result.value
            });
          }
          toast.success(`Request ${isApprove ? 'approved' : 'rejected'} successfully`);
          fetchRequests();
        } catch (error) {
          console.error(error);
          toast.error(error.response?.data?.message || `Failed to ${action} request`);
        }
      }
    });
  };

  const filteredRequests = requests.filter(r => {
    // Only show pending ones in the main list for admin
    if (r.status !== 'pending') return false;

    const matchesSearch =
      (r.employee && r.employee.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.project_site && r.project_site.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.reason && r.reason.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  return (
    <div className={styles['overtime-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'OT Requests', path: '/employees/ot-requests' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-left']}>
              <h1>Overtime Requests</h1>
              <p>Manage and review OT requests submitted by employees.</p>
            </div>
          </div>

          <div className={styles['summary-grid']}>
            {summaryData.map((data, index) => (
              <SummaryCard key={index} {...data} />
            ))}
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search size={18} className={styles['search-icon']} />
              <input
                type="text"
                placeholder="Search requests..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className={styles['data-view-container']}>
            <div className={styles['table-responsive']}>
              <table className={styles['data-table']}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Site & Date</th>
                    <th>Reason / Justification</th>
                    <th>Status</th>
                    <th className={styles['actions-cell']}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                        <Loader2 size={24} className={styles['spinner']} style={{ display: 'inline-block', marginRight: '8px' }} />
                        <span>Loading Overtime Requests...</span>
                      </td>
                    </tr>
                  ) : filteredRequests.map((record) => (
                    <tr key={record.id}>
                      <td>
                        <div className={styles['user-identity']}>
                          <div className={styles['avatar-small']} style={{ background: '#e0f2fe', color: '#0284c7' }}>
                            {record.employee ? record.employee.charAt(0) : 'E'}
                          </div>
                          <div className={styles['user-info']}>
                            <span className={styles['user-name']}>{record.employee}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles['user-info']}>
                          <span className={styles['user-site']} style={{ fontWeight: 600 }}>{record.project_site}</span>
                          <span className={styles['user-site']}><Calendar size={12} /> {formatDate(record.date)}</span>
                        </div>
                      </td>
                      <td>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>
                          "{record.reason}"
                        </p>
                      </td>
                      <td>
                        <span className={`${styles['status-badge']} ${getStatusClass(record.status)}`}>
                          {getStatusIcon(record.status)}
                          {getStatusLabel(record.status)}
                        </span>
                      </td>
                      <td className={styles['actions-cell']}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            className={styles['btn-action-success']}
                            onClick={() => handleAction(record, 'approve')}
                            title="Approve"
                          >
                            <CheckCircle2 size={18} />
                          </button>
                          <button
                            className={styles['btn-action-danger']}
                            onClick={() => handleAction(record, 'reject')}
                            title="Reject"
                          >
                            <XCircle size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && filteredRequests.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                        No pending OT requests available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>


            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={paginationData.current_page}
                totalItems={paginationData.total}
                itemsPerPage={paginationData.per_page}
                onPageChange={(page) => { setCurrentPage(page); }}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => { setCurrentPage(page); }}
            />
          </div>

          <Footer />
        </main>
      </div >
    </div >
  );
};

export default OTRequests;

